// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import Svg, { Circle, Line, Path, Rect, Text as SvgText } from 'react-native-svg';

import { Text, View } from '@/components/Themed';
import { seriesColor } from '@/theme/seriesColors';

import type { Player, TourTotalsByHoleResponse } from '../types';
import { buildMeritPositions, type RoundMerits } from '../positions';
import { styles } from '../styles';
import appColors, { alpha } from '@/theme/colors';

type Props = {
  players: Player[];
  series: TourTotalsByHoleResponse['series'];
  holes: TourTotalsByHoleResponse['holes'];
  rounds: TourTotalsByHoleResponse['rounds'];
  lastHoleIndexByPlayer?: Record<string, number>;
  /** Merits awarded per round; drives the standing the graph plots. */
  meritsByRound?: RoundMerits[];
  /** Merit total per player from the results table, incl. tour-long games. */
  totalMeritsByPlayerId?: Record<string, number>;
  /** Handicap per player, so players level on merits rank the way the table ranks them. */
  tieBreakHandicapByPlayerId?: Record<string, number>;
};

type Point = { x: number; y: number };

// Roughly how many holes to plot. A full tour is far more holes than the panel
// can show as distinct points, so we sample every nth hole (every hole when the
// tour is short enough) and keep the last hole so the curve ends on the standing.
const TARGET_POINTS = 20;

// Player and round ids reach this screen in a few spellings; the merit maps and
// the per-hole series only line up once both sides are normalized.
const normalizeId = (id: string) => id.trim().toLowerCase();

const normalizeKeys = (values: Record<string, number>) =>
  Object.fromEntries(Object.entries(values).map(([id, value]) => [normalizeId(id), value]));

const trimTrailingMissingTotals = (totals: number[]) => {
  let lastIndex = totals.length - 1;
  while (lastIndex >= 0) {
    if (Number.isFinite(totals[lastIndex])) break;
    lastIndex -= 1;
  }
  return lastIndex >= 0 ? totals.slice(0, lastIndex + 1) : [];
};

// Catmull-Rom through every point, converted to cubic beziers so the standings
// read as a soft curve instead of a zig-zag. Tension below 1 keeps the overshoot
// small, and the control points are clamped to the line's own span so a curve
// never swings past the best or worst position that player actually held.
const smoothPath = (points: Point[], tension = 0.8) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  const clampY = (value: number) => Math.min(maxY, Math.max(minY, value));
  let path = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const previous = points[i - 1] ?? points[i];
    const current = points[i];
    const next = points[i + 1];
    const after = points[i + 2] ?? next;
    const c1x = current.x + ((next.x - previous.x) / 6) * tension;
    const c1y = clampY(current.y + ((next.y - previous.y) / 6) * tension);
    const c2x = next.x - ((after.x - current.x) / 6) * tension;
    const c2y = clampY(next.y - ((after.y - current.y) / 6) * tension);
    path += ` C${c1x},${c1y} ${c2x},${c2y} ${next.x},${next.y}`;
  }
  return path;
};

// The x-axis walks the tour hole by hole (sampled down to ~20 points) and the
// y-axis is the leaderboard position at that hole, ranked on the cumulative
// total with standard competition ranking so tied players share a place.
export function PositionGraph({
  players,
  series,
  holes,
  rounds,
  lastHoleIndexByPlayer,
  meritsByRound,
  totalMeritsByPlayerId,
  tieBreakHandicapByPlayerId,
}: Props) {
  const [containerWidth, setContainerWidth] = useState(0);
  const width = useMemo(() => {
    if (containerWidth <= 0) {
      return 320;
    }
    return Math.max(280, containerWidth - 24);
  }, [containerWidth]);

  const nameLookup = new Map(players.map((p) => [p.id, p.name]));

  const normalizedSeries = useMemo(() => {
    const lastHoleIndexMap = lastHoleIndexByPlayer ?? {};
    return series
      .map((entry) => {
        const playerId = entry.playerId ?? entry.PlayerId ?? '';
        const trimmed = trimTrailingMissingTotals(entry.totals ?? entry.Totals ?? []);
        const cutoff = lastHoleIndexMap[playerId];
        const totals = cutoff === undefined ? trimmed : trimmed.slice(0, Math.min(cutoff + 1, trimmed.length));
        return { playerId, totals };
      })
      .filter((entry) => entry.playerId);
  }, [lastHoleIndexByPlayer, series]);

  const holeCount = Math.max(holes.length, ...normalizedSeries.map((entry) => entry.totals.length), 1);

  // One index per plotted hole: every hole when the tour is short, otherwise
  // every nth hole plus the final one.
  const sampleIndexes = useMemo(() => {
    const step = Math.max(1, Math.ceil(holeCount / TARGET_POINTS));
    const indexes: number[] = [];
    for (let index = 0; index < holeCount; index += step) {
      indexes.push(index);
    }
    if (indexes[indexes.length - 1] !== holeCount - 1) {
      indexes.push(holeCount - 1);
    }
    return indexes;
  }, [holeCount]);

  // Cumulative points carried forward over holes the player has not reached yet,
  // so ranking stays defined for everyone at every sampled hole.
  const positionSeries = useMemo(() => {
    const carried = normalizedSeries.map((entry) => {
      const values: number[] = [];
      let running = 0;
      for (let index = 0; index < holeCount; index += 1) {
        const value = entry.totals[index];
        if (Number.isFinite(value)) {
          running = value as number;
        }
        values.push(running);
      }
      return {
        playerId: entry.playerId,
        key: normalizeId(entry.playerId),
        values,
        playedThrough: entry.totals.length - 1,
      };
    });

    // The tour is won on merits, so rank on those whenever the leaderboard has
    // them. Without merits (an unsaved snapshot, or the very first holes of a
    // tour) fall back to the raw points race.
    const meritInput = meritsByRound?.map((entry) => ({
      roundId: normalizeId(entry.roundId),
      meritsByPlayerId: normalizeKeys(entry.meritsByPlayerId),
    }));
    if (meritInput && meritInput.length > 0) {
      const cumulativePoints: Record<string, number[]> = {};
      carried.forEach((entry) => {
        cumulativePoints[entry.key] = entry.values;
      });
      const { positionsBySample, totals } = buildMeritPositions({
        playerIds: carried.map((entry) => entry.key),
        cumulativePoints,
        roundIdByHole: holes.map((hole) => normalizeId(hole.roundId ?? hole.RoundId ?? '')),
        meritsByRound: meritInput,
        totalMerits: totalMeritsByPlayerId ? normalizeKeys(totalMeritsByPlayerId) : undefined,
        sampleIndexes,
        tieBreakHandicaps: tieBreakHandicapByPlayerId
          ? normalizeKeys(tieBreakHandicapByPlayerId)
          : undefined,
      });
      return carried
        .map((entry) => ({
          playerId: entry.playerId,
          total: totals[entry.key] ?? 0,
          playedThrough: entry.playedThrough,
          positions: positionsBySample[entry.key] ?? [],
        }))
        .sort((a, b) => b.total - a.total);
    }

    return carried
      .map((entry) => ({
        playerId: entry.playerId,
        total: entry.values[holeCount - 1] ?? 0,
        playedThrough: entry.playedThrough,
        positions: sampleIndexes.map((holeIndex) => {
          const own = entry.values[holeIndex] ?? 0;
          const ahead = carried.filter((other) => (other.values[holeIndex] ?? 0) > own).length;
          return ahead + 1;
        }),
      }))
      .sort((a, b) => b.total - a.total);
  }, [
    holeCount,
    holes,
    meritsByRound,
    normalizedSeries,
    sampleIndexes,
    tieBreakHandicapByPlayerId,
    totalMeritsByPlayerId,
  ]);

  const playerCount = Math.max(1, positionSeries.length);
  const height = Math.min(260, Math.max(160, 56 + playerCount * 28));
  const padding = 28;
  const labelPadding = 12;
  const originY = height - padding;
  const originX = padding;
  const plotWidth = Math.max(0, width - padding - labelPadding);
  const plotHeight = height - padding * 2;

  const maxPosition = Math.max(1, ...positionSeries.flatMap((entry) => entry.positions));
  const positionLabels = Array.from({ length: maxPosition }, (_, index) => index + 1);
  const positionLabelStep = maxPosition > 10 ? 2 : 1;

  const xFor = (holeIndex: number) =>
    holeCount > 1 ? originX + (holeIndex * plotWidth) / (holeCount - 1) : originX + plotWidth / 2;
  const yFor = (position: number) =>
    maxPosition > 1 ? padding + ((position - 1) * plotHeight) / (maxPosition - 1) : padding + plotHeight / 2;

  // Each round gets its own band so the reader can see where a round starts and
  // ends behind the curves.
  const roundRanges = useMemo(() => {
    const order: string[] = [];
    const ranges: Record<string, { start: number; end: number }> = {};
    holes.forEach((hole, index) => {
      const roundId = hole.roundId ?? hole.RoundId ?? '';
      if (!roundId) return;
      if (!ranges[roundId]) {
        ranges[roundId] = { start: index, end: index };
        order.push(roundId);
      } else {
        ranges[roundId].end = index;
      }
    });
    return order.map((roundId) => ({ roundId, ...ranges[roundId] }));
  }, [holes]);

  const roundLabelById = useMemo(
    () =>
      new Map(
        rounds.map((round) => {
          const id = round.roundId ?? round.RoundId ?? '';
          const name = round.name ?? round.Name ?? round.courseName ?? round.CourseName ?? '';
          return [id, name.length > 16 ? `${name.slice(0, 16)}…` : name];
        })
      ),
    [rounds]
  );

  const handleLayout = (event: LayoutChangeEvent) => {
    const nextWidth = event.nativeEvent.layout.width;
    if (nextWidth > 0 && Math.abs(nextWidth - containerWidth) > 1) {
      setContainerWidth(nextWidth);
    }
  };

  return (
    <View style={styles.graphWrap} onLayout={handleLayout}>
      <Svg width={width} height={height}>
        {roundRanges.map((range, index) => (
          <Rect
            key={`round-band-${range.roundId}`}
            x={xFor(range.start)}
            y={padding}
            width={Math.max(0, xFor(range.end) - xFor(range.start))}
            height={plotHeight}
            fill={index % 2 === 0 ? alpha(appColors.accent.glow, 0.22) : alpha(appColors.neutral.black, 0.18)}
          />
        ))}
        {positionLabels.map((position) => (
          <Line
            key={`pos-grid-${position}`}
            x1={originX}
            y1={yFor(position)}
            x2={width - labelPadding}
            y2={yFor(position)}
            stroke={alpha(appColors.text.primary, 0.14)}
            strokeWidth={1}
          />
        ))}
        {roundRanges.map((range, index) =>
          index === 0 ? null : (
            <Line
              key={`round-boundary-${range.roundId}`}
              x1={xFor(range.start)}
              y1={padding}
              x2={xFor(range.start)}
              y2={originY}
              stroke={alpha(appColors.text.primary, 0.45)}
              strokeWidth={1}
            />
          )
        )}
        <Line x1={originX} y1={padding} x2={originX} y2={originY} stroke={alpha(appColors.text.primary, 0.25)} strokeWidth={1} />
        {positionLabels.map((position) =>
          position === 1 || position === maxPosition || position % positionLabelStep === 0 ? (
            <SvgText
              key={`y-${position}`}
              x={originX - 6}
              y={yFor(position) + 4}
              fontSize="10"
              fill={appColors.text.primary}
              textAnchor="end"
            >
              {position}
            </SvgText>
          ) : null
        )}
        {positionSeries.map((entry, index) => {
          const color = seriesColor(index);
          // Tied players share a position; nudge each line a little so overlapping
          // strands stay individually visible.
          const tieOffset = (index - (positionSeries.length - 1) / 2) * 2;
          const points = sampleIndexes
            .map((holeIndex, sampleIndex) => ({ holeIndex, position: entry.positions[sampleIndex] }))
            .filter(({ holeIndex }) => holeIndex <= Math.max(0, entry.playedThrough))
            .map(({ holeIndex, position }) => ({ x: xFor(holeIndex), y: yFor(position) + tieOffset }));
          return (
            <Path
              key={entry.playerId}
              d={smoothPath(points)}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}
        {positionSeries.map((entry, index) => {
          const color = seriesColor(index);
          const tieOffset = (index - (positionSeries.length - 1) / 2) * 2;
          const lastSample = sampleIndexes.reduce(
            (best, holeIndex, sampleIndex) => (holeIndex <= Math.max(0, entry.playedThrough) ? sampleIndex : best),
            0
          );
          return (
            <Circle
              key={`${entry.playerId}-dot`}
              cx={xFor(sampleIndexes[lastSample])}
              cy={yFor(entry.positions[lastSample] ?? 1) + tieOffset}
              r={3}
              fill={color}
            />
          );
        })}
        {roundRanges.map((range) => {
          const label = roundLabelById.get(range.roundId);
          if (!label) return null;
          return (
            <SvgText
              key={`x-${range.roundId}`}
              x={xFor(range.start) + 2}
              y={originY + 14}
              fontSize="9"
              fill={appColors.text.primary}
              textAnchor="start"
            >
              {label}
            </SvgText>
          );
        })}
      </Svg>
      <View style={styles.graphLegend}>
        {positionSeries.map((entry, index) => {
          const color = seriesColor(index);
          const finalPosition = entry.positions[entry.positions.length - 1] ?? 0;
          return (
            <View key={entry.playerId} style={styles.legendItem}>
              <View style={[styles.legendSwatch, { backgroundColor: color }]} />
              <Text style={styles.legendLabel}>
                {nameLookup.get(entry.playerId) ?? entry.playerId} · #{finalPosition} ({entry.total})
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}
